/**
 * Histórico append-only de alterações do laudo (E23.F8 — ADR-0025). // ux-neutrality:allow doc-comment
 *
 * Lista cronológica de cada alteração confirmada: trecho, origem, quando e // ux-neutrality:allow doc-comment
 * antigo→novo. Read-only — a trilha nunca é editada. // ux-neutrality:allow doc-comment
 */
import { Blueprint, CardKicker, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { narrativeFieldLabel } from '@/lib/laudo_fields';
import type { EditSource, LaudoEditRecordData } from '@/lib/laudo_edits';

const DATE_FMT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function sourceLabel(source: EditSource): string {
  const t = ptBR.laudoCaso.editHistory;
  switch (source) {
    case 'manual':
      return t.sourceManual;
    case 'agent_proposed':
      return t.sourceAgentProposed;
    case 'human_override':
      return t.sourceHumanOverride;
    default:
      return source;
  }
}

function formatAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return ptBR.laudoCaso.editHistory.at.replace('{{date}}', DATE_FMT.format(date));
}

interface LaudoEditHistoryProps {
  edits: LaudoEditRecordData[];
}

export function LaudoEditHistory({ edits }: LaudoEditHistoryProps): JSX.Element {
  const t = ptBR.laudoCaso.editHistory;
  return (
    <Blueprint
      as="section"
      data-laudo-section="historico-edicoes"
      data-testid="laudo-edit-history"
      className="p-sc-4"
    >
      <CardKicker className="block">{t.kicker}</CardKicker>
      <h2 className="mt-1 text-body-md font-semibold text-fg">{t.title}</h2>
      {edits.length === 0 ? (
        <p className="mt-1 text-body-sm text-fg-muted" data-testid="laudo-edit-history-empty">
          {t.empty}
        </p>
      ) : (
        <ol className="mt-sc-2 flex flex-col gap-sc-3">
          {edits.map((edit) => (
            <li
              key={edit.id}
              className="border-l border-border pl-sc-3"
              data-testid="laudo-edit-history-item"
            >
              <p className="text-caption text-fg-muted">
                {t.entryField.replace('{{field}}', narrativeFieldLabel(edit.target_ref))} ·{' '}
                {sourceLabel(edit.source)}{' '}
                {/* Quando a alteração entrou na trilha é dado da cadeia de custódia: mono. */}
                <MonoData variant="muted" className="text-caption">
                  {formatAt(edit.created_at)}
                </MonoData>
              </p>
              <p className="mt-0.5 whitespace-pre-wrap text-body-sm text-fg-muted line-through">
                <span className="font-medium">{t.fromLabel}: </span>
                {edit.old_value}
              </p>
              <p className="mt-0.5 whitespace-pre-wrap text-body-sm text-fg">
                <span className="font-medium">{t.toLabel}: </span>
                {edit.new_value}
              </p>
            </li>
          ))}
        </ol>
      )}
    </Blueprint>
  );
}
